import React, { useState } from 'react';
import { Calendar, MapPin, ChevronRight, Briefcase, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { useLanguage, employerMissionsMap, employerSkillsMap } from '../i18n';

const EmployersPage = () => {
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [selectedMission, setSelectedMission] = useState(null);
  const [isEmployerDialogOpen, setIsEmployerDialogOpen] = useState(false);
  const [isMissionDialogOpen, setIsMissionDialogOpen] = useState(false);
  const { t, getEmployers, getMissions, getSkills, getMissionDetails } = useLanguage();
  
  const employers = getEmployers();
  const missions = getMissions();
  const skills = getSkills();
  const missionDetails = getMissionDetails();

  const handleEmployerClick = (employer) => {
    setSelectedEmployer(employer);
    setIsEmployerDialogOpen(true);
  };

  const handleMissionClick = (mission, e) => {
    e.stopPropagation();
    setSelectedMission(mission);
    setIsMissionDialogOpen(true);
  };

  const getEmployerMissions = (employerId) => {
    const missionIds = employerMissionsMap[employerId] || [];
    return missions.filter(m => missionIds.includes(m.id));
  };

  const getEmployerSkills = (employerId) => {
    const skillIds = employerSkillsMap[employerId] || [];
    return skills.filter(s => skillIds.includes(s.id));
  };

  const getEmployerName = (employerId) => {
    const employer = employers.find(e => e.id === employerId);
    return employer ? employer.name : employerId;
  };

  const getTechArray = (techString) => techString.split(', ');

  return (
    <Layout>
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12 lg:mb-16">
            <span className="label-mono mb-4 block">{t('employers.label')}</span>
            <h1 className="section-title">{t('employers.title')}</h1>
            <p className="font-body text-lg text-slate-600 mt-4 max-w-2xl">
              {t('employers.description')}
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute left-8 top-0 bottom-0 w-px bg-slate-200" />
            <div className="space-y-6">
              {employers.map((employer) => (
                <button key={employer.id} onClick={() => handleEmployerClick(employer)} className="group w-full text-left relative">
                  <div className="hidden lg:block absolute left-8 top-8 w-3 h-3 -ml-1.5 bg-blue-600 group-hover:scale-125 transition-transform" />
                  <div className="lg:ml-20 bg-white border border-slate-200 p-6 lg:p-8 hover:border-blue-600 transition-all duration-200">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-xs px-2 py-1 bg-blue-50 text-blue-600">{employer.type}</span>
                        </div>
                        <h3 className="font-display text-xl lg:text-2xl font-medium text-slate-800 mb-2">{employer.name}</h3>
                        <p className="font-body text-sm text-slate-600 mb-4">{employer.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" />
                            <span className="font-mono text-xs">{employer.duration}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4" />
                            <span className="font-mono text-xs">{employer.location}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Briefcase className="w-4 h-4" />
                            <span className="font-mono text-xs">{getEmployerMissions(employer.id).length} {t('skills.missions')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <Dialog open={isEmployerDialogOpen} onOpenChange={setIsEmployerDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white rounded-none">
              {selectedEmployer && (
                <>
                  <DialogHeader>
                    <span className="label-mono mb-2">{selectedEmployer.type}</span>
                    <DialogTitle className="font-display text-2xl font-semibold">{selectedEmployer.name}</DialogTitle>
                    <DialogDescription className="font-body text-slate-600">{selectedEmployer.description}</DialogDescription>
                  </DialogHeader>
                  <div className="mt-6 space-y-6">
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        <span className="font-mono text-sm">{selectedEmployer.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50">
                        <MapPin className="w-4 h-4 text-teal-600" />
                        <span className="font-mono text-sm">{selectedEmployer.location}</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">{t('employers.skillsApplied')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {getEmployerSkills(selectedEmployer.id).map((skill) => (
                          <Badge key={skill.id} className="font-mono text-xs rounded-none bg-blue-50 text-blue-600 border border-blue-200">{skill.name}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">{t('employers.missionsDelivered')} ({getEmployerMissions(selectedEmployer.id).length})</h4>
                      <div className="space-y-3">
                        {getEmployerMissions(selectedEmployer.id).map((mission) => (
                          <button 
                            key={mission.id} 
                            onClick={(e) => handleMissionClick(mission, e)}
                            className="w-full text-left p-4 bg-slate-50 border border-slate-200 hover:border-blue-600 hover:bg-blue-50/30 transition-all duration-200 group"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <h5 className="font-display font-medium text-slate-800 group-hover:text-blue-600 transition-colors">{mission.title}</h5>
                                <p className="font-body text-sm text-slate-600 mt-1">{mission.client} • {mission.sector}</p>
                                <p className="font-body text-sm text-slate-600 mt-2 line-clamp-2 text-justify">{mission.context}</p>
                              </div>
                              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">{t('employers.clientsServed')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {getEmployerMissions(selectedEmployer.id).map((mission) => (
                          <span key={mission.id} className="font-mono text-xs px-3 py-2 bg-white border border-slate-200 text-slate-700">{mission.client}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Mission Detail Dialog */}
          <Dialog open={isMissionDialogOpen} onOpenChange={setIsMissionDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white rounded-none">
              {selectedMission && (
                <>
                  <DialogHeader>
                    <div className="mb-2">
                      <span className="label-mono">{selectedMission.sector}</span>
                    </div>
                    <DialogTitle className="font-display text-2xl font-semibold">{selectedMission.title}</DialogTitle>
                    <DialogDescription className="font-body text-slate-600">
                      {selectedMission.client} • {getEmployerName(selectedMission.employer)}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-6 space-y-6">
                    <div>
                      <h4 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">{t('missions.businessContext')}</h4>
                      <p className="font-body text-slate-800 leading-relaxed text-justify">{selectedMission.context}</p>
                    </div>
                    <div>
                      <h4 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">{t('missions.responsibilities')}</h4>
                      <ul className="space-y-2">
                        {(missionDetails[selectedMission.id] || []).map((resp, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <span className="font-mono text-xs text-blue-600 mt-1.5">{String(index + 1).padStart(2, '0')}</span>
                            <span className="font-body text-slate-800">{resp}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-3">{t('missions.technologies')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {getTechArray(selectedMission.techs).map((tech) => (
                          <Badge key={tech} className="font-mono text-xs rounded-none bg-blue-50 text-blue-600 border border-blue-200">{tech}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      <span className="font-mono text-sm text-slate-500">{selectedMission.country}</span>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </Layout>
  );
};

export default EmployersPage;
